import useChatStore, {
  COLUMNS,
  GRID,
  ROWS,
  TABS,
} from '@/Components/Chat/store';
import AttachedFiles from '@/Features/Core/Components/AttachedFiles/AttachedFiles';
import {
  GridIcon,
  RowsIcon,
  TabsIcon,
  TreeIcon,
} from '@/Theme/icons/sideBySideIcons';
import ClearIcon from '@/Theme/icons/titleHeaderIcons/clearIcon';
import DeleteIcon from '@/Theme/icons/titleHeaderIcons/deleteIcon';
import DownloadIcon from '@/Theme/icons/titleHeaderIcons/downloadIcon';
import EditIcon from '@/Theme/icons/titleHeaderIcons/editIcon';
import {
  ArrowLeftIcon,
  EyeIcon,
  EyeSlashIcon,
  ShareIcon,
} from '@heroicons/react/24/solid';
import { IconButton, Tooltip } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { memo, useMemo } from 'react';

const getRenderIcon = (type, className = '', onClick) => {
  switch (type) {
    case TABS:
      return <TabsIcon className={className} onClick={onClick} />;
    case COLUMNS:
      return <TreeIcon className={className} onClick={onClick} />;
    case ROWS:
      return <RowsIcon className={className} onClick={onClick} />;
    case GRID:
      return <GridIcon className={className} onClick={onClick} />;
    default:
      break;
  }
};

const TitleBarChat = ({
  messages,
  setDeleted,
  goBackChat,
  setToDelete,
  setShowFileDialog,
  setShowEditDialog,
  setShowClearDialog,
  setShowDeleteDialog,
  setShowDownloadDialog,
}) => {
  const {
    refFile,
    renderType,
    hideOnHover,
    setShareChat,
    selectedChat,
    headerVisible,
    setRenderType,
    updateRefFile,
    setHideOnHover,
    selectedModels,
    maxMessageModels,
  } = useChatStore((state) => ({
    refFile: state.refFile,
    renderType: state.renderType,
    hideOnHover: state.hideOnHover,
    selectedChat: state.selectedChat,
    setShareChat: state.setShareChat,
    updateRefFile: state.updateRefFile,
    setRenderType: state.setRenderType,
    headerVisible: state.headerVisible,
    selectedModels: state.selectedModels,
    setHideOnHover: state.setHideOnHover,
    maxMessageModels: state.maxMessageModels,
  }));

  const renderTypes = useMemo(() => {
    const types = [
      { label: 'Tree view', type: COLUMNS, icon: TreeIcon },
      { label: 'Rows layout', type: ROWS, icon: RowsIcon },
      { label: 'Grid layout', type: GRID, icon: GridIcon },
    ];
    return maxMessageModels > 2 || selectedModels.length > 2
      ? types
      : types.filter((e) => e.type != GRID);
  }, [maxMessageModels, selectedModels]);

  return (
    <>
      {/* Title chat - Top Bar */}
      <AnimatePresence>
        {(messages?.length > 0 || selectedChat?.chat?.title) && (
          <>
            {headerVisible && (
              <motion.div
                key="title-actions"
                initial="hidden"
                animate="visible"
                exit="hidden"
                variants={{
                  hidden: { height: 0, opacity: 0 },
                  visible: { height: 32, opacity: 1 },
                }}
                transition={{ duration: 0.2 }}
                className="flex px-2 mx-auto w-full lg:max-w-4xl text-violet-blue dark:text-stargate-shimmer bg-lavender h-8 rounded-lg dark:bg-ship-grey"
              >
                <Tooltip title="Go back" arrow placement="bottom-end">
                  <div
                    onClick={goBackChat}
                    className="w-10 p-1 flex items-center justify-center hover:bg-ghost-white dark:hover:bg-palladium rounded cursor-pointer lg:hidden"
                  >
                    <ArrowLeftIcon className="h-5 w-5" />
                  </div>
                </Tooltip>

                {/* Side by side controls */}
                <div className="hidden md:flex items-center mr-3">
                  {renderType == TABS ? (
                    <>
                      {/* Side by side view */}
                      <Tooltip title="Side by side" arrow>
                        <div>
                          <TabsIcon
                            className="cursor-pointer select-none mx-[10px] w-5 h-5"
                            onClick={() => setRenderType(COLUMNS)}
                          />
                        </div>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      {/* Tabs view */}
                      <Tooltip title="Tabs layout" arrow>
                        <div>
                          {getRenderIcon(
                            renderType,
                            'cursor-pointer select-none mx-[10px] w-5 h-5',
                            () => setRenderType(TABS)
                          )}
                        </div>
                      </Tooltip>
                      {(maxMessageModels > 1 || selectedModels.length > 1) && (
                        <div className="flex gap-2 max-h-min px-2 py-1 rounded-lg bg-ghost-white text-cool-gray dark:bg-lead dark:text-stargate-shimmer">
                          {renderTypes.map((type, index) => (
                            <Tooltip key={index} title={type.label} arrow>
                              <div>
                                <type.icon
                                  className="w-4 h-4 cursor-pointer select-none"
                                  onClick={() => setRenderType(type.type)}
                                />
                              </div>
                            </Tooltip>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="w-full flex-1 flex items-center justify-center line-clamp-1">
                  <p className="text-base font-semibold leading-6 line-clamp-1">
                    {selectedChat?.chat?.title}
                  </p>
                </div>
                {!selectedChat?.chat?.isShared && (
                  <>
                    <Tooltip title="Share" arrow>
                      <IconButton onClick={() => setShareChat(true)}>
                        <ShareIcon
                          className={
                            'h-5 w-5 text-violet-blue dark:text-stargate-shimmer'
                          }
                        />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download" arrow>
                      <IconButton onClick={() => setShowDownloadDialog(true)}>
                        <DownloadIcon className="h-5 w-5 text-violet-blue dark:text-stargate-shimmer" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete" arrow>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleted(false);
                          setShowDeleteDialog(true);
                          setToDelete(selectedChat?.chat?._id);
                        }}
                      >
                        <DeleteIcon
                          className={
                            'h-5 w-5 text-violet-blue dark:text-stargate-shimmer'
                          }
                        />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit name" arrow>
                      <IconButton onClick={() => setShowEditDialog(true)}>
                        <EditIcon
                          className={
                            'h-5 w-5 text-violet-blue dark:text-stargate-shimmer'
                          }
                        />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Clear" arrow>
                      <IconButton onClick={() => setShowClearDialog(true)}>
                        <ClearIcon
                          className={
                            'h-5 w-5 text-violet-blue dark:text-stargate-shimmer'
                          }
                        />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={hideOnHover ? 'Show' : 'Hide'} arrow>
                      <IconButton onClick={() => setHideOnHover(!hideOnHover)}>
                        {hideOnHover ? (
                          <EyeSlashIcon
                            className={
                              'h-5 w-5 text-violet-blue dark:text-stargate-shimmer stroke-2'
                            }
                          />
                        ) : (
                          <EyeIcon
                            className={
                              'h-5 w-5 text-violet-blue dark:text-stargate-shimmer stroke-2'
                            }
                          />
                        )}
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </motion.div>
            )}
          </>
        )}
        {headerVisible && (
          <motion.div
            key="attachments"
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={{
              hidden: { height: 0, opacity: 0 },
              visible: { height: 32, opacity: 1 },
            }}
            transition={{ duration: 0.5 }}
            className="mx-auto w-full lg:max-w-4xl"
          >
            <AttachedFiles
              files={refFile}
              onFileChange={updateRefFile}
              setShowFileDialog={setShowFileDialog}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const areEqual = (prevProps, nextProps) => {
  return prevProps.messages === nextProps.messages;
};

export default memo(TitleBarChat, areEqual);
